import { useState, useCallback } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { compressImage, IMAGE_PRESETS } from '../lib/imageUtils';

/**
 * Photo Upload Hook
 *
 * Handles uploading photos to Firebase Storage with progress tracking.
 * Photos are compressed to 1600px max dimension at 90% quality for good clarity
 * on both thumbnails and detail pages.
 *
 * Storage path: raffle-photos/{userId}/{timestamp}-{filename}.jpg
 *
 * Security:
 * - Uses authenticated user's ID in path
 * - Storage rules should restrict access to owner
 */
export function usePhotoUpload() {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Upload a single photo to Firebase Storage
   *
   * @param {File} file - The file to upload
   * @param {function} onProgress - Progress callback (0-100)
   * @returns {Promise<string>} - The download URL
   */
  const uploadPhoto = useCallback(
    async (file, onProgress) => {
      if (!user) {
        throw new Error('Must be logged in to upload');
      }

      setError(null);

      // Compress image (1600px max, 90% quality)
      const compressedFile = await compressImage(file, IMAGE_PRESETS.full);

      // Generate unique filename
      const timestamp = Date.now();
      const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `raffle-photos/${user.uid}/${timestamp}-${baseName}.jpg`;

      console.log('[usePhotoUpload] Original:', file.name, (file.size / 1024).toFixed(0) + 'KB', '-> Compressed:', (compressedFile.size / 1024).toFixed(0) + 'KB');

      const storageRef = ref(storage, filePath);

      return new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, compressedFile, {
          contentType: compressedFile.type,
        });

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            onProgress?.(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            setError(error.message);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log('[usePhotoUpload] Download URL:', downloadURL);
              resolve(downloadURL);
            } catch (err) {
              console.error('Error getting download URL:', err);
              setError(err.message);
              reject(err);
            }
          }
        );
      });
    },
    [user]
  );

  /**
   * Upload multiple photos
   *
   * @param {File[]} files - Array of files to upload
   * @param {function} onProgress - Progress callback for each file
   * @returns {Promise<string[]>} - Array of download URLs
   */
  const uploadPhotos = useCallback(
    async (files, onProgress) => {
      setUploading(true);
      setError(null);

      try {
        const urls = await Promise.all(
          files.map((file, index) =>
            uploadPhoto(file, (progress) => onProgress?.(index, progress))
          )
        );
        return urls;
      } finally {
        setUploading(false);
      }
    },
    [uploadPhoto]
  );

  return {
    uploadPhoto,
    uploadPhotos,
    uploading,
    error,
  };
}

export default usePhotoUpload;
